import { rootApi } from '../rootApi';
import { formatDateForApi } from '../../utils/dateUtils';


const appendArrayToQueryParams = (queryParams, key, array) => {
  array?.forEach(item => queryParams.append(key, item));
};


const sanitizeRequestBody = (body) => {
    const sanitizers = {
        dealer: (val) => val?.length > 0 ? val : null,
        sasiNo: (val) => val?.length > 0 ? val : null,
        country: (val) => val?.length > 0 ? val : null,
        workOrderNo: (val) => (val != null && val !== '') ? Number(val) : null,
        dealerCode: (val) => val?.length > 0 ? val : null, 
        warrClaimNo: (val) => val || null,
        modelName: (val) => val || null,
        warrTypeList: (val) => val?.length > 0 ? val : null,
        workOrderNoStart: (val) => val ?? null,
        workOrderNoEnd: (val) => val ?? null,
        sentToApprovalStartDate: (val) => formatDateForApi(val) || null,
        sentToApprovalEndDate: (val) => formatDateForApi(val) || null,
        claimStatus: (val) => (val != null && val !== '') ? Number(val) : null,
        code: (val) => val || null,
        licensePlate: (val) => val || null,
        pageNumber: (val) => val ?? 1,
        pageSize: (val) => val ?? 10,
        languageId: (val) => val ?? null,
    };

    return Object.entries(sanitizers).reduce((acc, [key, sanitizer]) => {
        acc[key] = sanitizer(body?.[key]);
        return acc;
    }, {});
};

export const vehicleWarrantyClaimsReportApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getVehicleWarrantyClaims: builder.mutation({
            query: (body) => ({
                url: 'v1/vehiclewarrantyinformation/getallwarrantyclaims',
                method: 'POST',
                body: sanitizeRequestBody(body),
            }),
            transformResponse: (response) => {
                if (response?.data && Array.isArray(response.data.items)) {
                    return {
                        items: response.data.items,
                        totalCount: response.data.totalCount ?? 0,
                    };
                }
                return { items: [], totalCount: 0 };
            },
            invalidatesTags: ['VehicleWarrantyClaims']
        }),
        exportVehicleWarrantyClaimsToExcel: builder.query({
            // Don't cache blob - prevent serialization warnings
            keepUnusedDataFor: 0,
            query: (params) => {
                const sanitized = sanitizeRequestBody(params);
                const queryParams = new URLSearchParams();

                // Array parametreleri
                appendArrayToQueryParams(queryParams, 'dealer', sanitized.dealer);
                appendArrayToQueryParams(queryParams, 'sasiNo', sanitized.sasiNo);
                appendArrayToQueryParams(queryParams, 'country', sanitized.country);
                appendArrayToQueryParams(queryParams, 'warrTypeList', sanitized.warrTypeList);
                appendArrayToQueryParams(queryParams, 'dealerCode', sanitized.dealerCode);

                // Basit parametreler
                if (sanitized.warrClaimNo) {
                    queryParams.append('warrClaimNo', sanitized.warrClaimNo);
                }
                if (sanitized.workOrderNo != null) {
                    queryParams.append('workOrderNo', sanitized.workOrderNo);
                }
                if (sanitized.workOrderNoStart != null) {
                    queryParams.append('workOrderNoStart', sanitized.workOrderNoStart);
                }
                if (sanitized.workOrderNoEnd != null) {
                    queryParams.append('workOrderNoEnd', sanitized.workOrderNoEnd);
                }
                if (sanitized.modelName) {
                    queryParams.append('modelName', sanitized.modelName);
                }
                if (sanitized.code) {
                    queryParams.append('code', sanitized.code);
                }
                if (sanitized.licensePlate) {
                    queryParams.append('licensePlate', sanitized.licensePlate);
                }

                const startDate = sanitized.sentToApprovalStartDate;
                if (startDate) {
                    queryParams.append('sentToApprovalStartDate', startDate);
                }

                const endDate = sanitized.sentToApprovalEndDate;
                if (endDate) {
                    queryParams.append('sentToApprovalEndDate', endDate);
                }
                if (sanitized.claimStatus != null) {
                    queryParams.append('claimStatus', sanitized.claimStatus);
                }

                // LanguageId parametresi (yeni streaming endpoint için)
                if (params.languageId != null) {
                    queryParams.append('languageId', params.languageId);
                }

                const format = params.format ?? 'xlsx';
                queryParams.append('format', format);

                return {
                    url: `v1/vehiclewarrantyinformation/exportwarrantyclaimstoexcel?${queryParams.toString()}`,
                    method: 'GET',
                    responseHandler: async (response) => {
                        const blob = await response.blob();
                        const disposition = response.headers.get('Content-Disposition');
                        const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                        const fileName = match?.[1]?.replace(/['"]/g, '')
                            ?? `Garanti_Talepleri_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.${format}`;
                        return { blob, fileName };
                    }
                };
            },
            transformResponse: ({ blob, fileName }) => {
                const contentType = fileName.endsWith('.csv')
                    ? 'text/csv; charset=utf-8'
                    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                return { blob, fileName, contentType, recordCount: null };
            }
        })
    })
});

export const { useGetVehicleWarrantyClaimsMutation,  useLazyExportVehicleWarrantyClaimsToExcelQuery  } = vehicleWarrantyClaimsReportApi;
